import themes from 'devextreme/ui/themes';
import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { LayoutModule } from '@angular/cdk/layout';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { InitialNavigation, RouterModule } from '@angular/router';
import config from 'devextreme/core/config';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import { BrowserUtils } from '@azure/msal-browser';
import { ToastrModule } from 'ngx-toastr';

import {
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
} from './app/msal.config';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { ScreenService } from './app/shared/services';
import { environment, RuntimeConfig } from './environments/environment';
import { licenseKey } from './devextreme-license';
import { DemoApiInterceptor } from './app/shared/demo/demo-api.interceptor';

config({ licenseKey });

themes.initialized(() => {
    fetch('assets/config.json', { cache: 'no-cache' })
        .then(response => {
            if (!response.ok) {
                throw new Error(`Failed to load assets/config.json (${response.status})`);
            }
            return response.json() as Promise<RuntimeConfig>;
        })
        .then(runtimeConfig => {
            Object.assign(environment, runtimeConfig);

            if (environment.production) {
                enableProdMode();
            }

            return bootstrapApp();
        })
        .catch(err => console.error(err));
});

function bootstrapApp() {
    return bootstrapApplication(AppComponent, {
        providers: [
            importProvidersFrom(
                BrowserModule,
                RouterModule.forRoot(routes, {
                    // Don't perform initial navigation in iframes or popups
                    initialNavigation: !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup()
                        ? ('enabled' as InitialNavigation)
                        : undefined,
                }),
                LayoutModule,
                MsalModule,
                ToastrModule.forRoot()
            ),
            // Disable CDK Overlay's Popover API (top-layer) usage globally.
            // Angular CDK v21 defaults usePopover:true, placing dialogs in the browser top layer
            // which cannot be painted over by non-top-layer elements regardless of z-index.
            // Reverting to z-index-based stacking allows DevExtreme overlays to appear above Material dialogs.
            { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },

            // use legacy zone.js change-detection (default in ng 21 is to remove it)
            // will require code changes to be able to remove it
            provideZoneChangeDetection({ eventCoalescing: true }),

            provideNativeDateAdapter(),
            provideAnimationsAsync(),

            ScreenService,
            { provide: MAT_DATE_LOCALE, useValue: 'en-AU' },
            { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: { showDelay: 500 } },
            {
                provide: HTTP_INTERCEPTORS,
                useClass: DemoApiInterceptor,
                multi: true,
            },
            {
                provide: HTTP_INTERCEPTORS,
                useClass: MsalInterceptor,
                multi: true,
            },
            provideHttpClient(withInterceptorsFromDi()),
            {
                provide: MSAL_INSTANCE,
                useFactory: MSALInstanceFactory,
            },
            {
                provide: MSAL_GUARD_CONFIG,
                useFactory: MSALGuardConfigFactory,
            },
            {
                provide: MSAL_INTERCEPTOR_CONFIG,
                useFactory: MSALInterceptorConfigFactory,
            },
            MsalService,
            MsalGuard,
            MsalBroadcastService,
        ]
    });
}
